import { useEffect, useMemo, useState } from "react";
import { CalendarRange, KanbanSquare, LogIn, LogOut, Network, Plus, Sparkles, Table2 } from "lucide-react";
import { FilterBar } from "./components/FilterBar";
import { RoadmapView } from "./components/RoadmapView";
import { KanbanView } from "./components/KanbanView";
import { TableView } from "./components/TableView";
import { GraphView } from "./components/GraphView";
import { InitiativeForm } from "./components/InitiativeForm";
import { InitiativeDetailPanel } from "./components/InitiativeDetailPanel";
import { AdminLogin } from "./components/AdminLogin";
import { Button } from "./components/ui/button";
import { Select } from "./components/ui/input";
import { api, clearAdminPassword, getAdminPassword, getActor } from "./lib/api";
import { currentQuarterKey, listQuarters, nextQuarterKey } from "./lib/quarter";
import type { Filters, Initiative, InitiativeLink, Product, Team } from "./lib/types";
import { cn } from "./lib/utils";

type ViewMode = "roadmap" | "kanban" | "table" | "graph";

const EMPTY_FILTERS: Filters = {
  teams: new Set(),
  products: new Set(),
  statuses: new Set(),
  priorities: new Set(),
  search: "",
};

export default function App() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [links, setLinks] = useState<InitiativeLink[]>([]);
  const [allInitiatives, setAllInitiatives] = useState<Initiative[]>([]);
  const [quarter, setQuarter] = useState(nextQuarterKey());
  const [view, setView] = useState<ViewMode>("roadmap");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<Initiative | null>(null);
  const [editing, setEditing] = useState<Initiative | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(Boolean(getAdminPassword()));
  const [actor, setActorState] = useState(getActor());
  const [loading, setLoading] = useState(true);

  const quarterOptions = useMemo(() => listQuarters(new Date(), 1, 3), []);

  async function reloadAll() {
    const [t, p, all, q, lk] = await Promise.all([
      api.teams(),
      api.products(),
      api.initiatives(),       // all quarters — used for graph + detail panel cross-quarter links
      api.initiatives(quarter), // current quarter only — for filtered views
      api.links(),
    ]);
    setTeams(t);
    setProducts(p);
    setAllInitiatives(all);
    setInitiatives(q);
    setLinks(lk);
  }

  async function reloadLinks() {
    const lk = await api.links();
    setLinks(lk);
  }

  useEffect(() => {
    setLoading(true);
    reloadAll()
      .catch((e) => console.error("load failed", e))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarter]);

  const filtered = useMemo(() => {
    const s = filters.search.trim().toLowerCase();
    return initiatives.filter((i) => {
      if (filters.teams.size && !filters.teams.has(i.team_id)) return false;
      if (filters.products.size && !filters.products.has(i.product_id)) return false;
      if (filters.statuses.size && !filters.statuses.has(i.status)) return false;
      if (filters.priorities.size && !filters.priorities.has(i.priority)) return false;
      if (s) {
        const hay = `${i.title} ${i.description ?? ""} ${i.owner ?? ""} ${i.jira_key ?? ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [initiatives, filters]);

  // For graph: include cross-quarter initiatives only if they're connected to a visible one
  const graphInitiatives = useMemo(() => {
    const visibleIds = new Set(filtered.map((i) => i.id));
    const connectedIds = new Set<number>();
    for (const l of links) {
      if (visibleIds.has(l.from_id)) connectedIds.add(l.to_id);
      if (visibleIds.has(l.to_id)) connectedIds.add(l.from_id);
    }
    return allInitiatives.filter((i) => visibleIds.has(i.id) || connectedIds.has(i.id));
  }, [allInitiatives, filtered, links]);

  const stats = useMemo(() => {
    const byPriority = { P0: 0, P1: 0, P2: 0 };
    let inProgress = 0;
    for (const i of initiatives) {
      byPriority[i.priority]++;
      if (i.status === "in_progress") inProgress++;
    }
    return { total: initiatives.length, inProgress, ...byPriority };
  }, [initiatives]);

  async function handleSubmit(data: Partial<Initiative>) {
    if (editing) {
      await api.update(editing.id, data);
    } else {
      await api.create(data);
    }
    await reloadAll();
    setEditing(null);
  }

  async function handleDelete(i: Initiative) {
    await api.remove(i.id);
    setSelected(null);
    await reloadAll();
  }

  function logout() {
    clearAdminPassword();
    setIsAdmin(false);
  }

  return (
    <div className="min-h-screen app-bg">
      <header className="sticky top-0 z-20 border-b bg-background/80 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-priority-p2 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold leading-none">Solidgate Planning</h1>
              <p className="text-xs text-muted-foreground mt-0.5">Quarterly initiatives roadmap</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Select
              className="w-32"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
            >
              {quarterOptions.map((q) => (
                <option key={q} value={q}>
                  {q.replace(/(\d{4})-Q(\d)/, "Q$2 $1")}
                </option>
              ))}
            </Select>

            {isAdmin ? (
              <>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> New initiative
                </Button>
                <Button size="sm" variant="ghost" onClick={logout} title={`Signed in as ${actor || "PM"}`}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setLoginOpen(true)}>
                <LogIn className="h-4 w-4" /> PM sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Initiatives" value={stats.total} accent="from-primary/30 to-primary/0" />
          <StatCard label="In progress" value={stats.inProgress} accent="from-priority-p2/30 to-priority-p2/0" />
          <StatCard label="P0" value={stats.P0} accent="from-priority-p0/30 to-priority-p0/0" />
          <StatCard label="P1 / P2" value={`${stats.P1} / ${stats.P2}`} accent="from-priority-p1/30 to-priority-p1/0" />
        </section>

        <section className="rounded-xl border bg-card p-4">
          <FilterBar
            teams={teams}
            products={products}
            filters={filters}
            setFilters={setFilters}
          />
        </section>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {filtered.length} of {initiatives.length} initiatives
            {currentQuarterKey() === quarter && (
              <span className="ml-2 inline-block rounded-full bg-primary/15 text-primary border border-primary/30 px-2 py-0.5 text-xs">
                Current quarter
              </span>
            )}
          </div>
          <div className="inline-flex items-center rounded-lg border p-0.5 bg-card">
            <ViewBtn active={view === "roadmap"} onClick={() => setView("roadmap")}>
              <CalendarRange className="h-3.5 w-3.5" /> Roadmap
            </ViewBtn>
            <ViewBtn active={view === "graph"} onClick={() => setView("graph")}>
              <Network className="h-3.5 w-3.5" /> Graph
            </ViewBtn>
            <ViewBtn active={view === "kanban"} onClick={() => setView("kanban")}>
              <KanbanSquare className="h-3.5 w-3.5" /> Kanban
            </ViewBtn>
            <ViewBtn active={view === "table"} onClick={() => setView("table")}>
              <Table2 className="h-3.5 w-3.5" /> Table
            </ViewBtn>
          </div>
        </div>

        {loading ? (
          <div className="rounded-xl border bg-card p-12 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : view === "roadmap" ? (
          <RoadmapView
            initiatives={filtered}
            links={links}
            teams={teams}
            quarter={quarter}
            onSelect={setSelected}
          />
        ) : view === "graph" ? (
          <GraphView initiatives={graphInitiatives} links={links} onSelect={setSelected} />
        ) : view === "kanban" ? (
          <KanbanView initiatives={filtered} onSelect={setSelected} />
        ) : (
          <TableView initiatives={filtered} onSelect={setSelected} />
        )}

        <footer className="text-xs text-muted-foreground text-center pt-8 pb-4">
          Solidgate Planning · built on Cloudflare Pages + D1
        </footer>
      </main>

      <InitiativeDetailPanel
        initiative={selected}
        isAdmin={isAdmin}
        allInitiatives={allInitiatives}
        links={links}
        onClose={() => setSelected(null)}
        onEdit={(i) => {
          setEditing(i);
          setSelected(null);
          setFormOpen(true);
        }}
        onDelete={handleDelete}
        onLinksChanged={reloadLinks}
      />

      <InitiativeForm
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        initial={editing ?? undefined}
        teams={teams}
        products={products}
        defaultQuarter={quarter}
        onSubmit={handleSubmit}
      />

      <AdminLogin
        open={loginOpen}
        onOpenChange={setLoginOpen}
        onSuccess={() => {
          setIsAdmin(true);
          setActorState(getActor());
        }}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent: string;
}) {
  return (
    <div className={cn("relative overflow-hidden rounded-xl border bg-card p-4")}>
      <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br", accent)} />
      <div className="relative">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
      </div>
    </div>
  );
}

function ViewBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active ? "bg-secondary text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}
